/**
 * shiftDateTime — the single, timezone-authoritative way to resolve a shift's
 * real start/end instant on the frontend.
 *
 * THE BUG THIS FIXES (Atlanta 10:00 AM incident): countdowns and clock-in
 * windows were built with `new Date(`${date}T${time}`)` or
 * `new Date(y, mo, d, h, m)`, both of which interpret the wall-clock time in
 * the VIEWER'S device timezone. A 10:00 AM America/New_York shift viewed on a
 * phone set to Central time resolved to 11:00 ET, so "starts in X hours" was an
 * hour off and could make staff arrive late.
 *
 * Resolution order (most authoritative first):
 *   1. An explicit UTC instant from the backend (`*_utc`, ISO string with Z),
 *      which is computed DST-correctly via SHIFT_START_UTC_SQL and, later,
 *      from booking-level per-worker overrides.
 *   2. A booking-level per-worker override (start_time/end_time) + event tz.
 *   3. The master shift date + time + event IANA timezone.
 * Only if NO timezone can be determined do we fall back to browser-local
 * parsing (logged), so behaviour never silently regresses.
 */

/** Convert a wall-clock time in an IANA timezone to a UTC epoch (ms). DST-safe. */
export function zonedWallTimeToUtcMs(y, mo, d, h, mi, s, tz) {
  // Interpret the wall time as if it were UTC, then correct by the zone's
  // offset at that instant (handles EST vs EDT automatically).
  const asUtcGuess = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  let offset = 0;
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = {};
    for (const p of dtf.formatToParts(new Date(asUtcGuess))) parts[p.type] = p.value;
    const asZoned = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second),
    );
    offset = asZoned - asUtcGuess; // how far the zone is ahead of UTC at that instant
  } catch (_e) {
    offset = 0; // unknown tz → treat guess as UTC
  }
  return asUtcGuess - offset;
}

/** Parse "9:00 AM", "09:00", or "09:00:00" → {h, mi} (24h). Null if unparseable. */
export function parseClockTime(timeStr) {
  if (!timeStr) return null;
  const t = String(timeStr).trim();
  const ampm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const mi = parseInt(ampm[2], 10);
    const mer = ampm[3].toUpperCase();
    if (mer === 'PM' && h < 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return { h, mi };
  }
  const h24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) return { h: parseInt(h24[1], 10), mi: parseInt(h24[2], 10) };
  return null;
}

function pickTz(shift) {
  return shift?.timezone || shift?.event_timezone || shift?.venue_timezone || shift?.shift_timezone || null;
}

function pickDate(shift) {
  const d = shift?.shift_date || shift?.date || shift?.event_date;
  return d ? String(d).slice(0, 10) : null;
}

/**
 * Resolve the absolute start/end instant of a shift as a Date.
 * @param {object} shift  shift or booking object (booking-level overrides win)
 * @param {'start'|'end'} which
 * @returns {Date|null}
 */
export function resolveShiftInstant(shift, which = 'start') {
  if (!shift) return null;

  // 1) Explicit backend UTC instant (preferred — includes per-worker overrides).
  const utcField = which === 'end'
    ? (shift.end_utc || shift.utc_end_time || shift.end_datetime_utc)
    : (shift.start_utc || shift.utc_start_time || shift.start_datetime_utc);
  if (utcField) {
    const d = new Date(utcField);
    if (!Number.isNaN(d.getTime())) return d;
  }

  // 2/3) Wall-clock time (booking override wins) + event timezone.
  const timeStr = which === 'end' ? shift.end_time : shift.start_time;
  const dateStr = pickDate(shift);
  const parsed = parseClockTime(timeStr);
  if (!dateStr || !parsed) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const tz = pickTz(shift);
  if (tz) {
    return new Date(zonedWallTimeToUtcMs(y, mo, d, parsed.h, parsed.mi, 0, tz));
  }
  // Last-resort fallback: browser-local (should be rare — log once).
  if (typeof console !== 'undefined') {
    console.warn('[shiftDateTime] no event timezone on shift; falling back to device tz', shift?.id);
  }
  return new Date(y, (mo || 1) - 1, d || 1, parsed.h, parsed.mi, 0, 0);
}

/** Hours (float) until the shift start, in true event time. Null if unknown. */
export function hoursUntilStart(shift, nowMs = Date.now()) {
  const start = resolveShiftInstant(shift, 'start');
  if (!start) return null;
  return (start.getTime() - nowMs) / 3_600_000;
}

/** Minutes (int) between now and the shift's start/end instant. */
export function minutesUntil(shift, which = 'start', nowMs = Date.now()) {
  const inst = resolveShiftInstant(shift, which);
  if (!inst) return null;
  return Math.round((inst.getTime() - nowMs) / 60_000);
}
